import React, { useState } from 'react';
import { formatDate, getParams, isAdmin } from '../data/utils';
import { AiOutlineEye } from 'react-icons/ai';
import Header from '../components/Header';
import Modal from '../components/Modal';
import Dropdown from '../components/Dropdown';
import DataGrid from '../components/DataGrid';
import Pagination from '../components/Pagination';
import { ActionButton } from '../components/Button';
import { useFetch } from '../hooks/useFetch';
import { useStateContext } from '../contexts/ContextProvider';
import { usePatch } from '../hooks/usePatch';
import 'react-photo-view/dist/react-photo-view.css';
import { PhotoView, PhotoProvider } from 'react-photo-view';

export const getStatusBadge = (status) => {
    const statusMap = {
        'NEW': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Nouveau' },
        'UNDER_REVIEW': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'En traitement' },
        'ASSIGNED': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Assigné' },
        'RESOLVED': { bg: 'bg-green-100', text: 'text-green-800', label: 'Résolu' },
        'CLOSED': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Cloturé' }
    };
    const config = statusMap[status] || statusMap['PENDING'];
    return (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}>
            {config.label}
        </span>
    );
};

const Alerts = () => {
    const { getCategory } = useStateContext();
    const [selectedAlert, setSelectedAlert] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalType, setModalType] = useState('view');
    const [params, setParams] = useState({
        page: 1,
        limit: 25,
        status: '',
        category: '',
        sortBy: 'createdAt'
    });

    const { loading, data: alerts, totalItems, totalPages } = useFetch('/alerts', params, []);
    const { loading: userLoading, data: usersData } = useFetch('/users');
    const { loading: patchLoading, putRequest } = usePatch()

    const handleViewAlert = async (alert) => {
        setSelectedAlert(alert);
        setModalType('view');
        setIsModalOpen(true);
    };


    const handleParamsChange = (key, value) => {
        setParams(getParams(key, value, params));
    };

    const handleUpdateStatus = (alertId, newStatus, assignedTo) => {
        putRequest(`/alerts/${alertId}/status`, { status: newStatus, assignedTo }).then((data) => {
            // Actualiser la liste des alertes après la mise à jour
            setSelectedAlert(prev => ({ ...prev, status: newStatus, assignedTo }));
            setParams({ ...params })
        });
    };




    const StatusTemplate = (props) => getStatusBadge(props.status);

    const CategoryTemplate = (props) => (
        <span className="text-sm">{getCategory(props?.categoryId)?.title}</span>
    );

    const DateTemplate = (props) => (
        <span className="text-sm">{formatDate(props.createdAt)}</span>
    );

    const AlertActionTemplate = (props) => (
        <div className='flex gap-2 justify-center'>
            <ActionButton
                onClick={() => handleViewAlert(props)}
                icon={AiOutlineEye}
                className='text-blue-600 border-blue-500 hover:bg-blue-500 hover:text-white'
            />
        </div>
    );

    // Options pour les dropdowns
    const sortOptions = [
        { label: 'Date de création', value: 'createdAt' },
        { label: 'Date de mise à jour', value: 'updatedAt' },
        { label: 'Titre', value: 'title' },
        { label: 'Statut', value: 'status' },
    ];

    const statusOptions = [
        { label: 'Nouveau', value: 'NEW', bg: 'bg-yellow-100', text: 'text-yellow-800' },
        { label: 'Assigné', value: 'ASSIGNED', bg: 'bg-purple-100', text: 'text-purple-800' },
        { label: 'En Traitement', value: 'UNDER_REVIEW', bg: 'bg-blue-100', text: 'text-blue-800' },
        { label: 'Résolu', value: 'RESOLVED', bg: 'bg-green-100', text: 'text-green-800' },
        { label: 'Cloturé', value: 'CLOSED', bg: 'bg-gray-100', text: 'text-gray-800' },
    ];

    // Colonnes pour le DataGrid
    const alertsColumns = [
        {
            headerText: 'Catégorie',
            width: 150,
            textAlign: 'Center',
            template: CategoryTemplate,
            allowEditing: false,
        },
        {
            headerText: 'Statut',
            width: 130,
            textAlign: 'Center',
            template: StatusTemplate,
            allowEditing: false,
        },
        {
            headerText: 'Date',
            width: 120,
            textAlign: 'Center',
            template: DateTemplate,
            allowEditing: false,
        },
    ];

    const isLoading = loading || userLoading

    return (
        <div className='md:m-10 mt-auto p-2 pb-4 md:p-10 bg-gray-200 md:rounded-3xl rounded-xl w-100'>
            <Header
                category='Alertes'
                title=''
                refresh={() => setParams({ ...params })}
            />

            <div className='grid grid-cols-1 md:grid-cols-5 gap-4 mb-4'>
                <Dropdown
                    label='Trier par'
                    options={sortOptions}
                    value={params.sortBy}
                    onChange={(val) => handleParamsChange('sortBy', val)}
                />
                <Dropdown
                    label='Statut'
                    options={statusOptions}
                    value={params.status}
                    onChange={(val) => handleParamsChange('status', val)}
                />
            </div>

            <DataGrid
                data={alerts}
                columns={alertsColumns}
                allowPaging={false}
                loading={isLoading}
                editing={{ allowDeleting: false, allowEditing: false }}
                toolbarOptions={[]}
                actionButtons={AlertActionTemplate}
                extraComponents={
                    <Pagination
                        currentPage={params.page}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        onPageChange={(val) => handleParamsChange('page', val)}
                    />
                }
            />

            {isModalOpen && selectedAlert && (
                <Modal
                    show={isModalOpen}
                    title={modalType === 'view' ? 'Détails de l\'alerte' : 'Modifier l\'alerte'}
                    onClose={() => setIsModalOpen(false)}
                    size="lg"
                >
                    <div className="space-y-4">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {selectedAlert.title}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Créé le {formatDate(selectedAlert.createdAt)}
                            </p>
                        </div>
                        {
                            isAdmin() && (
                                <div>
                                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Assigne a</span>
                                    <Dropdown
                                        label="Assigné à"
                                        searchable={true}
                                        showAllOption={false}
                                        onSearch={(searchText) => {
                                            // Implémenter la recherche des utilisateurs ici si nécessaire
                                        }}
                                        options={usersData?.map(user => ({ label: user.fullName, value: user.id })) || []}
                                        value={selectedAlert.assignedTo}
                                        onChange={(val) => { handleUpdateStatus(selectedAlert.id, 'ASSIGNED', val) }}
                                    />
                                </div>
                            )
                        }


                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Catégorie:</span>
                                <p className="text-sm text-gray-900 dark:text-white">
                                    {getCategory(selectedAlert.categoryId).title}
                                </p>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Statut:</span>
                                <div className="mt-1">
                                    {getStatusBadge(selectedAlert.status)}
                                </div>
                            </div>
                        </div>

                        <div>
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Description:</span>
                            <p className="text-sm text-gray-900 dark:text-white mt-1">
                                {selectedAlert.description}
                            </p>
                        </div>

                        <div>
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Localisation:</span>
                            <p className="text-sm text-gray-900 dark:text-white mt-1">
                                {selectedAlert.location.displayName}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Lat: {selectedAlert.location.latitude}, Lng: {selectedAlert.location.longitude}
                            </p>
                        </div>
                        {
                            selectedAlert.medias && selectedAlert.medias.length > 0 && (
                                <div>
                                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Images:</span>
                                    <PhotoProvider toolbarRender={({ rotate, onRotate, scale, onScale }) => {
                                        return (
                                            <>
                                                <svg className="PhotoView-Slider__toolbarIcon" onClick={() => onScale(scale + 1)} width="44" height="44" viewBox="0 0 768 768" fill="white"><path d="M384 640.5q105 0 180.75-75.75t75.75-180.75-75.75-180.75-180.75-75.75-180.75 75.75-75.75 180.75 75.75 180.75 180.75 75.75zM384 64.5q132 0 225.75 93.75t93.75 225.75-93.75 225.75-225.75 93.75-225.75-93.75-93.75-225.75 93.75-225.75 225.75-93.75zM415.5 223.5v129h129v63h-129v129h-63v-129h-129v-63h129v-129h63z"></path></svg>
                                                <svg className="PhotoView-Slider__toolbarIcon" onClick={() => onScale(scale - 1)} width="44" height="44" viewBox="0 0 768 768" fill="white"><path d="M384 640.5q105 0 180.75-75.75t75.75-180.75-75.75-180.75-180.75-75.75-180.75 75.75-75.75 180.75 75.75 180.75 180.75 75.75zM384 64.5q132 0 225.75 93.75t93.75 225.75-93.75 225.75-225.75 93.75-225.75-93.75-93.75-225.75 93.75-225.75 225.75-93.75zM223.5 352.5h321v63h-321v-63z"></path></svg>
                                                <svg className="PhotoView-Slider__toolbarIcon" onClick={() => onRotate(rotate + 90)} width="44" height="44" fill="white" viewBox="0 0 768 768"><path d="M565.5 202.5l75-75v225h-225l103.5-103.5c-34.5-34.5-82.5-57-135-57-106.5 0-192 85.5-192 192s85.5 192 192 192c84 0 156-52.5 181.5-127.5h66c-28.5 111-127.5 192-247.5 192-141 0-255-115.5-255-256.5s114-256.5 255-256.5c70.5 0 135 28.5 181.5 75z"></path></svg>
                                            </>
                                        )
                                    }}>
                                        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4">
                                            {selectedAlert.medias.map((media, index) => (
                                                <PhotoView key={index} src={media.url}>
                                                    <img
                                                        src={media.url}
                                                        alt={`Media ${index + 1}`}
                                                        className="w-full h-32 object-cover rounded-md cursor-pointer hover:opacity-80 transition"
                                                    />
                                                </PhotoView>
                                            ))}
                                        </div>
                                    </PhotoProvider>

                                </div>
                            )
                        }

                        {modalType === 'view' && (
                            <div className="flex justify-end gap-2 pt-4">

                                <button
                                    onClick={() => handleUpdateStatus(selectedAlert.id, 'UNDER_REVIEW')}
                                    className="px-4 py-2 bg-blue-100 text-blue-800 rounded-md hover:bg-blue-600"
                                    disabled={['UNDER_REVIEW', 'RESOLVED', 'CLOSED'].includes(selectedAlert.status) || patchLoading}
                                >
                                    Marquer en traitement
                                </button>

                                <button
                                    onClick={() => handleUpdateStatus(selectedAlert.id, 'RESOLVED')}
                                    className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
                                    disabled={['NEW', 'ASSIGNED', 'RESOLVED', 'CLOSED'].includes(selectedAlert.status) || patchLoading}
                                >
                                    Marquer comme résolu
                                </button>
                                <button
                                    onClick={() => handleUpdateStatus(selectedAlert.id, 'CLOSED')}
                                    className={`px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600`}
                                    disabled={['NEW', 'ASSIGNED', 'CLOSED'].includes(selectedAlert.status) || patchLoading}
                                >
                                    Marquer comme Cloturé
                                </button>
                            </div>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default Alerts;